<script setup lang="ts">
import { ref } from 'vue'
import { NButton, NEmpty } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { shortThread } from '../composables/shortThread'
import type { AgentSessionSummary } from '../stores/agent'

// The cross-channel session list for /agent, extracted from AgentView
// so the view stays under the file-size limit and the list can render
// both inline (desktop sidebar) and inside a drawer (mobile) from one source.
// Per-row rename + delete are inline so the list needs no app-level
// dialog provider; the parent owns the actual store mutations via emits.
defineProps<{
  sessions: AgentSessionSummary[]
  activeThreadId: string | null
  isEmpty: boolean
  // Global agent-session devcontainer slot pool usage (#1352/#1353) — null
  // while unloaded/unavailable, in which case the indicator just doesn't
  // render. Global count only, no per-session/per-repo breakdown by design.
  slotUsage?: { in_use: number; capacity: number } | null
  // Thread whose stop is in flight (~10s of synchronous teardown) — that
  // row's Stop button shows a spinner and blocks a second fire (#1381).
  stoppingThreadId?: string | null
}>()

const emit = defineEmits<{
  open: [threadId: string]
  'new-session': []
  rename: [threadId: string, name: string]
  delete: [threadId: string]
  stop: [threadId: string]
  info: [session: AgentSessionSummary]
}>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

// Format cents as $X.XX (mirrors UsageView's formatUsd). Below $0.01
// collapses to "<$0.01" so a cheap session doesn't read as "$0.00" (issue #654).
function formatCost(cents: number): string {
  if (cents === 0) return '$0.00'
  if (cents < 1) return '<$0.01'
  return `$${(cents / 100).toFixed(2)}`
}

// Local inline-edit + delete-confirm state (one row at a time).
const editingId = ref<string | null>(null)
const editValue = ref('')
const confirmingId = ref<string | null>(null)

function startRename(session: AgentSessionSummary) {
  confirmingId.value = null
  editingId.value = session.thread_id
  editValue.value = session.name ?? ''
}

function commitRename() {
  const id = editingId.value
  const name = editValue.value.trim()
  editingId.value = null
  // Ignore an empty rename — keep the existing name rather than clearing it.
  if (id && name) emit('rename', id, name)
}

function startDelete(threadId: string) {
  editingId.value = null
  confirmingId.value = threadId
}

function confirmDelete(threadId: string) {
  confirmingId.value = null
  emit('delete', threadId)
}
</script>

<template>
  <div class="flex flex-col gap-2" data-testid="agent-session-list">
    <div class="flex items-center justify-between gap-2">
      <h2 class="text-sm font-semibold text-white/80 m-0">Sessions</h2>
      <NButton v-if="activeThreadId" size="tiny" tertiary data-testid="agent-new-session" @click="emit('new-session')">
        + New session
      </NButton>
    </div>
    <!-- At capacity the next run fails outright with "all slots in use", so
         escalate the line instead of letting a full pool read as fine. -->
    <p
      v-if="slotUsage"
      class="text-xs m-0"
      :class="slotUsage.in_use >= slotUsage.capacity ? 'text-amber-300' : 'text-white/40'"
      data-testid="agent-slot-usage"
    >
      {{ slotUsage.in_use }} of {{ slotUsage.capacity }} devcontainer slots in use<template
        v-if="slotUsage.in_use >= slotUsage.capacity"
      >
        — new sessions fail until one frees up</template
      >
    </p>
    <NEmpty v-if="isEmpty" size="small" description="No active sessions yet" class="py-2" />
    <ul v-else class="m-0 p-0 list-none flex flex-col gap-1 max-h-[55vh] overflow-y-auto pr-1">
      <li v-for="s in sessions" :key="s.thread_id" class="flex items-stretch gap-1">
        <input
          v-if="editingId === s.thread_id"
          :ref="
            (el) => {
              if (el) (el as HTMLInputElement).focus()
            }
          "
          v-model="editValue"
          class="flex-1 min-w-0 bg-white/10 text-white text-sm rounded px-2 py-2 outline-none border border-brand-primary/40"
          data-testid="agent-session-rename-input"
          @keydown.enter.prevent="commitRename"
          @keydown.esc.prevent="editingId = null"
          @blur="commitRename"
        />
        <button
          v-else
          class="flex-1 text-left px-2 py-2 rounded border min-w-0"
          :class="
            s.thread_id === activeThreadId
              ? 'bg-brand-primary/10 border-brand-primary/30 text-white'
              : 'border-transparent hover:bg-white/10 text-white/70'
          "
          :aria-current="s.thread_id === activeThreadId ? 'true' : undefined"
          data-testid="agent-session-item"
          @click="emit('open', s.thread_id)"
        >
          <span class="flex items-center gap-1.5 min-w-0">
            <span
              v-if="s.running"
              class="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse"
              data-testid="agent-session-running"
              aria-label="running"
            />
            <span class="text-sm truncate" data-testid="agent-session-name">{{
              s.name || shortThread(s.thread_id)
            }}</span>
          </span>
          <span class="flex items-center justify-between gap-2 text-xs text-white/40">
            <span class="font-mono truncate">{{ s.repo || 'superbrain' }}</span>
            <span class="flex items-center gap-2 shrink-0">
              <span
                v-if="s.total_cost_cents != null"
                class="tabular-nums"
                title="Estimated total cost for this session"
                data-testid="agent-session-cost"
                >{{ formatCost(s.total_cost_cents) }}</span
              >
              <span
                v-if="s.last_activity_at"
                class="tabular-nums"
                :title="formatAbsoluteDate(s.last_activity_at)"
                data-testid="agent-session-time"
                >{{ formatDate(s.last_activity_at, now) }}</span
              >
            </span>
          </span>
        </button>

        <div v-if="editingId !== s.thread_id" class="flex shrink-0 items-center gap-0.5">
          <template v-if="confirmingId === s.thread_id">
            <NButton
              size="tiny"
              type="error"
              :aria-label="`Confirm delete session ${s.name || shortThread(s.thread_id)}`"
              data-testid="agent-session-delete-confirm"
              @click="confirmDelete(s.thread_id)"
            >
              Delete?
            </NButton>
            <NButton
              size="tiny"
              tertiary
              :aria-label="`Cancel deleting session ${s.name || shortThread(s.thread_id)}`"
              @click="confirmingId = null"
              ><span aria-hidden="true">✕</span></NButton
            >
          </template>
          <template v-else>
            <NButton
              size="tiny"
              tertiary
              :aria-label="`Devcontainer info for ${s.name || shortThread(s.thread_id)}`"
              data-testid="agent-session-info"
              @click="emit('info', s)"
            >
              <span aria-hidden="true">ⓘ</span>
            </NButton>
            <NButton
              size="tiny"
              tertiary
              :aria-label="`Rename session ${s.name || shortThread(s.thread_id)}`"
              data-testid="agent-session-rename"
              @click="startRename(s)"
            >
              <span aria-hidden="true">✎</span>
            </NButton>
            <NButton
              size="tiny"
              tertiary
              :loading="stoppingThreadId === s.thread_id"
              :disabled="stoppingThreadId === s.thread_id"
              :title="`Stop this session's container — its slot frees up, but the conversation stays`"
              :aria-label="`Stop session ${s.name || shortThread(s.thread_id)}`"
              data-testid="agent-session-stop"
              @click="emit('stop', s.thread_id)"
            >
              <span aria-hidden="true">⏸</span>
            </NButton>
            <NButton
              size="tiny"
              tertiary
              :aria-label="`Delete session ${s.name || shortThread(s.thread_id)}`"
              data-testid="agent-session-delete"
              @click="startDelete(s.thread_id)"
            >
              <span aria-hidden="true">✕</span>
            </NButton>
          </template>
        </div>
      </li>
    </ul>
  </div>
</template>
